import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { Icon } from '@/icons/Icon'
import { Loader } from '@/components/Loader'

import { message, pDel, pGet, pPatch, pPost, type PlatformAdminRow } from './api'
import { usePlatformAuth } from './auth'
import { generateHandoverPassword, hashFor } from './handover'
import { HandoverPassword } from './HandoverPassword'

/**
 * The people who may sign in to this portal, and nobody else may.
 *
 * A platform administrator is not a carrier's user: separate table, separate
 * sign-in, separate cookie, its own host. The one thing you cannot do here is
 * switch off or delete your own account - which is also what keeps the portal
 * from being emptied, because whoever is calling is signed in and so at least
 * one account is always left (`app/api/v1/platform.py`).
 */

interface Listing {
  admins: PlatformAdminRow[]
  activeAdmins: number
}

export default function AdminsPage() {
  const qc = useQueryClient()
  const { me } = usePlatformAuth()
  const [adding, setAdding] = useState(false)
  const [email, setEmail] = useState('')
  const [fullName, setFullName] = useState('')
  const [handover, setHandover] = useState<{ who: string; password: string } | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null)

  const listing = useQuery({ queryKey: ['pf', 'admins'], queryFn: () => pGet<Listing>('/admins') })
  const invalidate = () => void qc.invalidateQueries({ queryKey: ['pf', 'admins'] })

  const add = useMutation({
    mutationFn: async () => {
      const addr = email.trim().toLowerCase()
      const password = generateHandoverPassword()
      await pPost<PlatformAdminRow>('/admins', {
        email: addr,
        fullName: fullName.trim(),
        password: await hashFor(password, addr),
      })
      return { who: addr, password }
    },
    onSuccess: (result) => {
      setHandover(result)
      setAdding(false)
      setEmail('')
      setFullName('')
      invalidate()
    },
  })

  const reset = useMutation({
    mutationFn: async (row: PlatformAdminRow) => {
      const password = generateHandoverPassword()
      await pPost<PlatformAdminRow>(`/admins/${row.id}/password`, {
        password: await hashFor(password, row.email),
      })
      return { who: row.email, password }
    },
    onSuccess: (result) => {
      setHandover(result)
      invalidate()
    },
  })

  const setActive = useMutation({
    mutationFn: ({ row, isActive }: { row: PlatformAdminRow; isActive: boolean }) =>
      pPatch<PlatformAdminRow>(`/admins/${row.id}`, { isActive }),
    onSuccess: invalidate,
  })

  const remove = useMutation({
    mutationFn: (row: PlatformAdminRow) => pDel(`/admins/${row.id}`),
    onSuccess: () => {
      setConfirmDelete(null)
      invalidate()
    },
  })

  const rows = listing.data?.admins ?? []
  const error = add.error ?? reset.error ?? setActive.error ?? remove.error

  return (
    <>
      <div className="pf-head">
        <div>
          <h1 className="pf-head__h">Platform administrators</h1>
          <p className="pf-head__d">Everybody who can sign in to this portal.</p>
        </div>
        <div className="pf-head__acts">
          <button className="ix-btn ix-btn--compact" type="button" onClick={() => setAdding((v) => !v)}>
            <Icon name={adding ? 'chevron-right' : 'plus'} size={14} />
            {adding ? 'Cancel' : 'New administrator'}
          </button>
        </div>
      </div>

      {adding ? (
        <section className="st-panel pf-sec" style={{ marginBottom: 18 }}>
          <header className="st-panel__head">
            <div>
              <div className="st-panel__t">New platform administrator</div>
              <div className="st-panel__d">Belongs to no carrier. No organization is created.</div>
            </div>
          </header>
          <form
            className="st-panel__body"
            onSubmit={(e) => {
              e.preventDefault()
              if (add.isPending || !email.trim() || !fullName.trim()) return
              add.mutate()
            }}
          >
            <div className="st-grid">
              <label className="st-field">
                <span>Email</span>
                <input
                  className="ix-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </label>
              <label className="st-field">
                <span>Name</span>
                <input
                  className="ix-input"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  required
                />
              </label>
            </div>
            <div className="st-form__actions">
              <button className="ix-btn ix-btn--primary" type="submit" disabled={add.isPending}>
                {add.isPending ? 'Creating…' : 'Create'}
              </button>
            </div>
          </form>
        </section>
      ) : null}

      {handover ? (
        <section className="st-panel pf-sec" style={{ marginBottom: 18 }}>
          <div className="st-panel__body">
            <HandoverPassword who={handover.who} password={handover.password} />
            <div className="st-form__actions">
              <button className="ix-btn ix-btn--secondary" type="button" onClick={() => setHandover(null)}>
                Close
              </button>
            </div>
          </div>
        </section>
      ) : null}

      {error ? (
        <div className="ix-alert ix-alert--error" role="alert" style={{ marginBottom: 14 }}>
          <div>{message(error)}</div>
        </div>
      ) : null}

      <div className="pf-card">
        <div className="pf-card__scroll">
        <table className="ix-table">
          <thead>
            <tr>
              <th>Account</th>
              <th>Added by</th>
              <th>Last signed in</th>
              <th>Status</th>
              <th className="pf-num">Actions</th>
            </tr>
          </thead>
          <tbody>
            {listing.isLoading ? (
              <tr>
                <td colSpan={5}>
                  <div className="pf-loading">
                    <Loader size="md" label="Loading administrators" />
                  </div>
                </td>
              </tr>
            ) : (
              rows.map((row) => {
                const isMe = row.id === me?.admin.id
                const locked = row.lockedUntil && new Date(row.lockedUntil) > new Date()
                return (
                  <tr key={row.id}>
                    <td>
                      <div className="st-user">
                        <span className="ix-avatar ix-avatar--sm">
                          {row.fullName.slice(0, 2).toUpperCase()}
                        </span>
                        <span className="st-user__id">
                          <span className="st-user__n">
                            {row.fullName}
                            {isMe ? <span className="st-user__you">You</span> : null}
                          </span>
                          <span className="st-user__e">{row.email}</span>
                        </span>
                      </div>
                    </td>
                    <td>{row.createdByName || <span className="st-muted">—</span>}</td>
                    <td className="pf-nowrap">
                      {row.lastLoginAt ? (
                        new Date(row.lastLoginAt).toLocaleDateString()
                      ) : (
                        <span className="st-muted">Never</span>
                      )}
                    </td>
                    <td>
                      {!row.isActive ? (
                        <span className="ix-chip ix-chip--error">Deactivated</span>
                      ) : locked ? (
                        <span className="ix-chip ix-chip--warning">Locked out</span>
                      ) : row.mustChangePassword ? (
                        <span className="ix-chip ix-chip--warning">Temporary</span>
                      ) : (
                        <span className="ix-chip ix-chip--success">Active</span>
                      )}
                    </td>
                    <td>
                      <div className="pf-rowacts">
                        <button
                          className="pf-act"
                          type="button"
                          disabled={reset.isPending}
                          onClick={() => reset.mutate(row)}
                        >
                          <Icon name="key" size={13} />
                          New password
                        </button>
                        {isMe ? (
                          // Your own account is the one you cannot switch off.
                          // Saying so beats a button that always refuses - but
                          // it is a sentence, not a third button, so it is not
                          // styled as one.
                          <span className="pf-note">Only another administrator</span>
                        ) : confirmDelete === row.id ? (
                          <>
                            <button
                              className="pf-act pf-act--danger"
                              type="button"
                              disabled={remove.isPending}
                              onClick={() => remove.mutate(row)}
                            >
                              Delete for good
                            </button>
                            <button className="pf-act" type="button" onClick={() => setConfirmDelete(null)}>
                              Keep
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              className="pf-act"
                              type="button"
                              disabled={setActive.isPending}
                              onClick={() => setActive.mutate({ row, isActive: !row.isActive })}
                            >
                              {row.isActive ? 'Deactivate' : 'Reactivate'}
                            </button>
                            <button
                              className="pf-act pf-act--danger"
                              type="button"
                              onClick={() => setConfirmDelete(row.id)}
                            >
                              <Icon name="trash-2" size={13} />
                              Delete
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
        </div>
      </div>
    </>
  )
}
